/**
 * Download audit log viewer — read-only trail of every export
 * (manual + scheduled) for the caller's company.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Loader2 } from 'lucide-react';
import { reportingAPI, type DashboardDownloadLogEntry } from '../../../lib/api/reporting';

interface DownloadAuditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function DownloadAuditDialog({ open, onOpenChange }: DownloadAuditDialogProps) {
  const [rows, setRows] = useState<DashboardDownloadLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await reportingAPI.listDownloadLogs({ page_size: 100 });
      setRows(response.data.results || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Download audit</DialogTitle>
          <DialogDescription>
            Every report export from your company. Newest first.
          </DialogDescription>
        </DialogHeader>

        <div className="border rounded-md overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">No downloads recorded.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[170px]">When</TableHead>
                  <TableHead>Dashboard</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="w-[90px]">Format</TableHead>
                  <TableHead className="w-[100px]">Source</TableHead>
                  <TableHead className="w-[90px] text-right">Size</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">
                      {new Date(r.downloaded_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs">{r.dashboard_name}</TableCell>
                    <TableCell className="text-xs">
                      {r.user_name || r.user_email || '—'}
                    </TableCell>
                    <TableCell className="text-xs uppercase">{r.format}</TableCell>
                    <TableCell className="text-xs capitalize">{r.source}</TableCell>
                    <TableCell className="text-xs text-right">{formatBytes(r.byte_size)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
